import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {noop} from 'lodash';

import {
  type ConsolidatedBindingContext,
  type BindingSource,
} from '@packages/systems/dynamo/gateway';
import {Expressions, matchExpression, Plugins} from '@packages/systems/wfdl';
import {
  rightSidebarWidth,
  rightSidebarLabelWidth,
} from '@packages/systems/design-system/constants/DesignerUI';
import PropertyList, {
  PropertyListAddButton,
  PropertyListItem,
  PropertyListItemID,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {Popover, PopoverContent} from '@packages/systems/spring';
import {parseSorts} from '@packages/systems/dynamo/utils/ParamUtils';
import {emotionStyled} from '@packages/utilities/styled';
import {
  View,
  InfoIcon,
  Select,
  Text,
  HStack,
  type SelectProps,
  Spacer as SpringSpacer,
  Tooltip,
  WarningTriangleIcon,
  Note,
  NoteBody,
} from '@packages/systems/spring';
import {FieldListIcon} from '@packages/systems/ui/shared-ui/FieldListIcon';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionBinding,
  ExpressionBindMetaPayload,
  ExpressionEditorActionPayload,
} from '@packages/systems/ExpressionEditor-utils';
import {EList, ERecord, EText, Expression} from '@packages/systems/wfdl-ast';
import {
  bindingSourceToSortablePropertyOption,
  getSortableSources,
  handleRandomSortOption,
  hasRandomSort,
  isRandomSort,
  SortablePropertyOption,
  textForSort,
} from '@packages/systems/dynamo/utils/DynSortUtils';
import {deleteAt, moveElement} from '@packages/utilities/array';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {SymbolBindingDot} from '@packages/systems/ui/shared-ui/SymbolBindingDot';
import {identity} from '@packages/utilities/fp/utils';

import {getFlexStyles} from '../collectionListUtils';

const Spacer = emotionStyled('div')({
  height: '$2',
});

const TwoColumns = emotionStyled('div')({
  display: 'grid',
  gridTemplateColumns: `${rightSidebarLabelWidth}px minmax(0, auto)`,
  gridGap: '$2',
  alignItems: 'center',
});

type SortItem = {property: string; ascending: boolean};
const ensureId = (
  item: SortItem,
  index: number
): {
  item: SortItem;
  id: string;
} => ({
  item,
  id: String(index),
});

const getSortOptions = (
  sources: Array<BindingSource>,
  items: Array<SortItem>
): Array<SortablePropertyOption> => {
  let sortableOptions = sources.map(bindingSourceToSortablePropertyOption);

  sortableOptions = handleRandomSortOption(items, sortableOptions);

  return sortableOptions;
};

const getSortOrders = (property: string, sources: Array<BindingSource>) => {
  const bindingSource = sources.find((source) => {
    const {path} = source;
    return path.length === 1 && path[0] && path[0].slug === property;
  });
  const bindingSourceType = bindingSource?.type;

  return [
    {
      value: true,
      text: textForSort({
        property,
        ascending: true,
        bindingSourceType,
      }),
    },
    {
      value: false,
      text: textForSort({
        property,
        ascending: false,
        bindingSourceType,
      }),
    },
  ];
};

type SortListItemProps = {
  listItem: {item: SortItem; id: string};
  idx: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  sortableProperties: Array<SortablePropertyOption>;
  bindingContext: ConsolidatedBindingContext | null;
  expressionEditorDispatch: (action: ExpressionEditorActionPayload) => void;
  sortExpr: Expression;
  usedProperties: Map<string, number>;
  itemCount: number;
};

const SortListItem = ({
  listItem,
  idx,
  isOpen,
  onOpenChange,
  sortableProperties,
  bindingContext,
  expressionEditorDispatch,
  sortExpr,
  usedProperties,
  itemCount,
}: SortListItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const sortOrders = bindingContext
    ? getSortOrders(listItem.item.property, getSortableSources(bindingContext))
    : [];
  const isRandomSortApplied = isRandomSort(listItem.item.property);
  const isDuplicateSortProp =
    usedProperties.has(listItem.item.property) &&
    usedProperties.get(listItem.item.property) !== idx;

  const handleOnFieldChange: SelectProps<typeof fieldOptions>['onChange'] = (
    fieldValue
  ) => {
    const currentList = Expressions.getList(sortExpr) || [];
    currentList[idx] = ERecord({
      field: EText(fieldValue),
      direction: EText(listItem.item.ascending ? 'asc' : 'desc'),
    });
    expressionEditorDispatch({
      type: 'EXPRESSION_ACTION',
      payload: {
        newExpression: EList(currentList),
      },
      key: [],
    });
  };

  const handleOnOrderChange: SelectProps<typeof orderOptions>['onChange'] = (
    orderValue
  ) => {
    const currentList = Expressions.getList(sortExpr) || [];
    currentList[idx] = ERecord({
      field: EText(listItem.item.property),
      direction: EText(orderValue === 'true' ? 'asc' : 'desc'),
    });
    expressionEditorDispatch({
      type: 'EXPRESSION_ACTION',
      payload: {
        newExpression: EList(currentList),
      },
      key: [],
    });
  };

  const fieldOptions = sortableProperties.map(({value, name, type}) => {
    const fieldType = typeof type === 'string' ? type : 'PlainText';

    return {
      value,
      label: name,
      icon: <FieldListIcon type={fieldType} />,
    };
  });

  const orderOptions = sortOrders.map((order) => ({
    value: String(order.value),
    label: order.text,
  }));

  const sortProperty = sortableProperties.find(
    (property) => listItem.item.property === property.value
  );

  if (!sortProperty) {
    return (
      <PropertyListItem
        data-automation-id="sort-item"
        key={idx}
        id={String(idx)}
        label={<span>Missing field</span>}
        onMouseOver={noop}
        onMouseOut={noop}
        onFocus={noop}
        onBlur={noop}
        icon={null}
      />
    );
  }

  const ascending = listItem.item.ascending;
  const sources = bindingContext ? getSortableSources(bindingContext) : [];
  const labelBindingSource = sources.find((source) => {
    const {path} = source;
    return path.length === 1 && path[0] && path[0].slug === sortProperty.value;
  });
  const orderText = bindingContext
    ? textForSort({
        property: sortProperty.value,
        ascending,
        bindingSourceType: labelBindingSource?.type,
      })
    : '';

  const labelContent = isRandomSort(listItem.item.property) ? (
    <>
      <Text colorVariant="secondary">Random</Text> <Text>shuffle</Text>
    </>
  ) : (
    <View display="flex" alignItems="center" gap="$1" minWidth="0" width="100%">
      <Text
        colorVariant="secondary"
        style={getFlexStyles(sortProperty.name.length, orderText.length)}
      >
        {sortProperty.name}
      </Text>{' '}
      <Text style={getFlexStyles(orderText.length, sortProperty.name.length)}>
        {orderText}
      </Text>
    </View>
  );

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      anchorRef={anchorEl}
      shouldShowMask
      maskProps={{colorVariant: 'transparent'}}
      shouldTrapFocus={false}
    >
      <PropertyListItem
        _ref={setAnchorEl}
        data-automation-id="sort-item"
        id={String(idx)}
        label={labelContent}
        onClick={itemCount > 0 ? () => onOpenChange(true) : undefined}
        onMouseOver={noop}
        onMouseOut={noop}
        onFocus={noop}
        onBlur={noop}
        icon={null}
        trailingIcon={
          isDuplicateSortProp && (
            <Tooltip
              label="This field is already sorted"
              placement="bottom-start"
              minWidth={200}
            >
              <WarningTriangleIcon
                data-automation-id="sort-item-duplicate-warning"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          )
        }
      />
      <PopoverContent isSeamless>
        <View
          padding="$2"
          width={rightSidebarWidth}
          data-automation-id="sort-item-popover"
        >
          <TwoColumns data-automation-id="sort-field-select-wrapper">
            <span>Field</span>
            <Select
              id="sort-field"
              aria-label="Select a field"
              data-automation-id="sort-field-select"
              onChange={handleOnFieldChange}
              options={fieldOptions}
              value={listItem.item.property}
              shouldPositionInline
              shouldTruncateButtonText
              optionTruncationProps={{
                label: {truncateToLines: 1},
              }}
            />
          </TwoColumns>
          <Spacer />
          {isRandomSortApplied ? (
            <Note icon={InfoIcon} colorVariant="primary">
              <NoteBody>Remove the random sort to sort by a field.</NoteBody>
            </Note>
          ) : (
            <TwoColumns>
              <span>Order</span>
              <Select
                id="sort-direction"
                aria-label="Select an order"
                data-automation-id="sort-order-select"
                onChange={handleOnOrderChange}
                options={orderOptions}
                value={String(listItem.item.ascending)}
                shouldTruncateButtonText
                shouldPositionInline
              />
            </TwoColumns>
          )}
          {isDuplicateSortProp && (
            <>
              <Spacer />
              <Note
                icon={WarningTriangleIcon}
                colorVariant="warning"
                data-automation-id="sort-panel-duplicate-note"
              >
                <NoteBody>This field is already sorted.</NoteBody>
              </Note>
            </>
          )}
        </View>
      </PopoverContent>
    </Popover>
  );
};

export const DynListSortingSettings = ({
  expressionEditorDispatch,
  bindingContext,
  expr,
  exprAsUnique,
  plugins,
  defaultValueExpr,
}: {
  expressionEditorDispatch: (action: ExpressionEditorActionPayload) => void;
  bindingContext: ConsolidatedBindingContext | null;
  expr: ContextualExpression;
  exprAsUnique: ContextualExpression;
  plugins: Plugins.PluginsType;
  defaultValueExpr: Expression;
}) => {
  const [itemIndex, setItemIndex] = useState<number>(-1);
  const [items, setItems] = useState<Array<SortItem>>([]);
  const [sortableProperties, setSortableProperties] = useState<
    Array<SortablePropertyOption>
  >([]);
  const [addDisabled, setAddDisabled] = useState(false);
  const [sortExpr, setSortExpr] = useState<Expression>(() => stripAll(expr));

  useEffect(() => {
    const newSortExpr = stripAll(expr);
    setSortExpr(newSortExpr);
    // Set the sort items to state from element
    const sortFields = Expressions.evaluate(
      Plugins.getEvalEnv(plugins),
      newSortExpr
    );
    if (sortFields && sortFields.length && sortFields.length > 0) {
      const parsedItems = parseSorts(sortFields);
      setItems(parsedItems.toJS());
    } else {
      setItems([]);
    }
  }, [expr, plugins]);

  useEffect(() => {
    // Set the sortable properties to state from binding context
    if (bindingContext) {
      const sources = getSortableSources(bindingContext);

      const sortOptions = getSortOptions(sources, items);
      setSortableProperties(sortOptions);
    } else {
      setSortableProperties([]);
    }
  }, [bindingContext, items]);

  React.useEffect(() => {
    // If the list has a random sort, adding more sort items
    // is disabled
    setAddDisabled(hasRandomSort(items));
  }, [items]);

  const usedProperties = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach((item, index) => {
      if (!map.has(item.property)) {
        map.set(item.property, index);
      }
    });
    return map;
  }, [items]);

  const openAddItemModal = useCallback(() => {
    // Exit if no sortable properties exist
    if (sortableProperties.length === 0) return;

    // Find the first unused property, or default to the first property if all are used
    const nextSortingField =
      sortableProperties.find(
        (property) => !usedProperties.has(property.value)
      ) ?? sortableProperties[0];

    if (!nextSortingField) return;

    // Set the index to the length of the array. Once the new
    // item is added the length will be +1, which is the index
    // we want to open.
    setItemIndex(items.length);
    const currentList = Expressions.getList(sortExpr) || [];
    expressionEditorDispatch({
      type: 'EXPRESSION_ACTION',
      payload: {
        newExpression: EList([
          ...currentList,

          ERecord({
            field: EText(nextSortingField.value),
            direction: EText('asc'),
          }),
        ]),
      },
      key: [],
    });
  }, [
    expressionEditorDispatch,
    items,
    sortExpr,
    sortableProperties,
    usedProperties,
  ]);

  const deleteItem = useCallback(
    (index: PropertyListItemID) => {
      const currentList = deleteAt(
        Expressions.getList(sortExpr) || [],
        Number(index)
      );
      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        key: [],
        payload: {
          newExpression: EList(currentList),
        },
      });
    },
    [expressionEditorDispatch, sortExpr]
  );

  const shiftListItem = useCallback(
    ({
      itemId,
      dropTargetId,
    }: {
      itemId: PropertyListItemID;
      dropTargetId: PropertyListItemID;
    }) => {
      const currentList = moveElement(
        Expressions.getList(sortExpr) || [],
        Number(itemId),
        Number(dropTargetId)
      );
      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        key: [],
        payload: {
          newExpression: EList(currentList),
        },
      });
    },
    [expressionEditorDispatch, sortExpr]
  );

  const renderListItem = useCallback(
    (listItem: {item: SortItem; id: string}, idx: number) => {
      return (
        <SortListItem
          key={idx}
          listItem={listItem}
          idx={idx}
          isOpen={idx === itemIndex}
          onOpenChange={(open) => setItemIndex(open ? idx : -1)}
          sortableProperties={sortableProperties}
          bindingContext={bindingContext}
          expressionEditorDispatch={expressionEditorDispatch}
          sortExpr={sortExpr}
          usedProperties={usedProperties}
          itemCount={items.length}
        />
      );
    },
    [
      bindingContext,
      expressionEditorDispatch,
      itemIndex,
      items.length,
      sortExpr,
      sortableProperties,
      usedProperties,
    ]
  );

  const sortItems = items.map(ensureId);

  const onBindingChange = (
    updatedBinding: null | Expression,
    meta: ExpressionBindMetaPayload
  ) => {
    if (updatedBinding === null) {
      // atm there is no datatype defined for DynamoWrapper so EXPRESSION_BIND_CHANGED cannot reset it to default
      // clearing up value manually We also need to ensure we are purely resetting to a List, not a Distinct
      const defaultValueExprNoDistinct = matchExpression({
        Distinct: ({value}) => value,
        _: identity,
      })(defaultValueExpr);

      expressionEditorDispatch({
        type: 'EXPRESSION_ACTION',
        payload: {
          newExpression: defaultValueExprNoDistinct,
          meta,
        },
        key: [],
      });
    } else {
      expressionEditorDispatch({
        type: 'EXPRESSION_BIND_CHANGED',
        binding: updatedBinding,
        meta,
        key: [],
      });
    }
  };

  if (ContextualExpressions.isBinding(stripAnn(expr))) {
    return (
      <View>
        <HStack data-automation-id="collection-list-settings-sorts-group">
          <Text
            colorVariant="secondary"
            style={{
              width: rightSidebarLabelWidth,
              minWidth: rightSidebarLabelWidth,
            }}
          >
            Sort
          </Text>
          <ExpressionBinding
            expression={exprAsUnique}
            onChange={onBindingChange}
            bindUIVariant="dot"
            automationLabel="Sort"
            style={{width: '100%'}}
          ></ExpressionBinding>
        </HStack>
      </View>
    );
  }

  return (
    <View>
      <HStack data-automation-id="collection-list-settings-sorts-group">
        <Text colorVariant="secondary">Sort</Text>
        <ExpressionBinding
          expression={exprAsUnique}
          onChange={onBindingChange}
          bindUIVariant="dot"
          automationLabel="Sort"
          button={(openPopover, targetRef) => (
            <Hitarea>
              <SymbolBindingDot
                ref={targetRef as React.RefObject<HTMLDivElement>}
                data-automation-id="binding-button_Sort"
                clickable
                onClick={openPopover}
                style={{
                  position: 'absolute',
                  top: -10,
                  left: -8,
                }}
              />
            </Hitarea>
          )}
        ></ExpressionBinding>
        <SpringSpacer />
        <PropertyListAddButton
          disabled={addDisabled}
          tooltip="Add list item"
          data-automation-id="collection-list-settings-add-sorting"
          onClick={openAddItemModal}
        />
      </HStack>
      {sortItems.length > 0 && (
        <View marginTop="$1">
          <PropertyList
            automationId="collection-list-settings-sorting-list"
            sortable
            deletable
            onItemDeleted={deleteItem}
            onItemDropped={shiftListItem}
            items={sortItems}
            renderItem={renderListItem}
          />
        </View>
      )}
    </View>
  );
};
